import React from 'react';
import { FormattedMessage } from 'react-intl';
import classNames from 'classnames';

import { IPlan, PromoCodeDiscount } from 'types';
import { Icon } from '../Icon/Icon';

export interface LabelAndAmount {
  id: string;
  label: string;
  amount: number;
}

interface Props {
  monthlyBaseFee: number;
  employeePayments: LabelAndAmount[];
  chosenPlan: IPlan;
  extras: LabelAndAmount[];
  setupFees: LabelAndAmount[];
  discounts: LabelAndAmount[];
  pricePerEmployee: number;
  totalAmountLabel: string;

  promoCodeDiscount?: PromoCodeDiscount;
  isSmall?: boolean;
}

export const PricingView = (props: Props) => {
  const {
    chosenPlan,
    promoCodeDiscount,
    monthlyBaseFee,
    pricePerEmployee,
    employeePayments,
    extras,
    setupFees,
    totalAmountLabel,
    discounts,
    isSmall,
  } = props;

  const {
    title,
  } = chosenPlan;

  const showPricingFeature = !!promoCodeDiscount;

  return (
    <div className={classNames('Pricing', { 'Pricing--small': isSmall })}>

      <div className="Pricing-header">
        <h3 className="Pricing-yourPlan">
          <FormattedMessage id="YOUR_PLAN" />
        </h3>
        <p className="Pricing-plan">
          {title}
        </p>
      </div>

      <div className="Pricing-body">
        <div className="Pricing-group">
          <p className="Pricing-title Pricing-group-label">
            <FormattedMessage id="MONTHLY_BASE_FEE" />
            <span className="Pricing-amount">${monthlyBaseFee.toFixed(2)}</span>
          </p>
        </div>

        {/* Chosen plan and employee amount breakdown */}
        <div className="Pricing-group">
          <p className="Pricing-title">
            <FormattedMessage id="AVERAGE_FEES_PER_MONTH" />
          </p>

          <p className="Pricing-group-label">
            <FormattedMessage id="PRICE_PER_EMPLOYEE" />
            <span className="Pricing-amount Pricing-amount--grey">${pricePerEmployee}</span>
          </p>

          {employeePayments.map(({ label, amount, id }) => (
            <p className="Pricing-group-label" key={id}>
              {label}
              <span className="Pricing-amount">${amount.toFixed(2)}</span>
            </p>
          ))}

          {/* <p className="Pricing-group-label" style={{ color: 'purple' }}>
            DEBUG Total cost
            <span className="Pricing-amount">${employeePayments.reduce((accumulator, { amount }) => accumulator + amount, 0).toFixed(2)}</span>
          </p> */}
        </div>

        {/* Extras */}
        {extras.length > 0 && (
          <div className="Pricing-group">
            {extras.map(({ label, amount, id }) => (
              <p key={id} className="Pricing-title Pricing-group-label">
                + {label}
                <span className="Pricing-amount">${amount.toFixed(2)}</span>
              </p>
            ))}
          </div>
        )}

        {/* Setup fees */}
        <div className="Pricing-group">
          {setupFees.map(({ label, amount, id }) => (
            <p className="Pricing-title Pricing-group-label" key={id}>
              {label}
              <span className="Pricing-amount">${amount.toFixed(2)}</span>
            </p>
          ))}
        </div>

        {/* Discounts */}
        {(discounts.length > 0 || showPricingFeature) && (
          <div className="Pricing-group">
            <p className="Pricing-title">
              <FormattedMessage id="DISCOUNTS" />
            </p>

            {discounts.map(({ label, amount, id }) => (
              <p className="Pricing-group-label" key={id}>
                {label}
                <span className="Pricing-amount">-${amount.toFixed(2)}</span>
              </p>
            ))}

            {showPricingFeature && (
              <div className="Pricing-feature">
                <Icon name="ic-present" />

                <div className="Pricing-feature-column">
                  {promoCodeDiscount && (
                    <div className="Pricing-feature-block">
                      <p className="Pricing-feature-title">
                        {promoCodeDiscount.title}
                      </p>
                      <p>
                        {promoCodeDiscount.description}
                      </p>
                    </div>
                  )}
                </div>

              </div>
            )}
          </div>
        )}

        {/* Total */}
        <div className="Pricing-group Pricing-group-label">
          <p className="Pricing-total">
            <FormattedMessage id="TOTAL" />
          </p>

          <div className="Pricing-total-group">
            <span className="Pricing-total-amount">{totalAmountLabel}</span>
            <span className="Pricing-total-gst">
              <FormattedMessage id="PLUS_GST" />
            </span>
          </div>
        </div>

        <div className="Pricing-disclaimer">
          <FormattedMessage tagName="p" id="PRICING_DISCLAIMER" />
        </div>
      </div>

    </div>
  );
};
